import * as React from "react";
import lodashGet from "lodash/get";
import { Dialog } from "components/dialog";
import { formatMessage } from "lib/globalize-util";
import Button from "components/form/button";
import { merge, $ } from "glamor";
import { Form } from "formsy-react";
import SelectField from "components/form/select-field";
import MenuItem from "material-ui/MenuItem";
import Checkbox from "material-ui/Checkbox";
import { ISchemaKey } from "helpers/product-collection";
import {
    DimensionParamsActionType,
    Order,
    filtersToShowToFiltersToHideArray,
    filtersListToFiltersToShowObject,
    PURCHASE_ORDER_DOOR_GROUP_FIELD,
    processActionsToDimensionParams,
} from "helpers/door-collection";
import { SchemaField } from "constants/product-collection";
import IndeterminateCheckBox from "material-ui/svg-icons/toggle/indeterminate-check-box";
import Divider from "material-ui/Divider";
import theme from "styles/theme";
import { List, Map } from "immutable";
import { IPurchaseOrderDoorGroup } from "helpers/delivery-purchase-order";
import { DimensionsPanelReduxState } from "reducers/dimensions-panel/dimensions-panel";

enum SelectionTypes {
    None = "none",
    Some = "some",
    All = "all",
}

const SELECT_ALL = "selectAll";

interface IManageDoorGroupingProps {
    open: boolean;
    onToggleManageDoorGrouping: () => void;
    doorClassificationSchema: ISchemaKey[];
    doorGrouping: string;
    groupingClassificationHide: string[];
    groupingClassificationOrder: Order;
    dimensionsPanelApplied: DimensionsPanelReduxState;
    onApply: (updatedState: DimensionsPanelReduxState) => void;
    onChangeCurrentView: (currentView: List<Map<string, any>>) => void;
    itemDisplayProperty?: List<any>;
    merchantPurchaseOrderDoorGroups: IPurchaseOrderDoorGroup[];
}

export const ManageDoorGrouping: React.FunctionComponent<IManageDoorGroupingProps> = ({
    open,
    onToggleManageDoorGrouping,
    doorClassificationSchema,
    doorGrouping,
    groupingClassificationOrder,
    groupingClassificationHide,
    dimensionsPanelApplied,
    onApply,
    onChangeCurrentView,
    itemDisplayProperty,
    merchantPurchaseOrderDoorGroups,
}) => {
    const styles = React.useMemo(() => {
        return {
            actions: merge(
                $(" .applyButton", {
                    marginLeft: "20px !important",
                })
            ),
            content: merge(
                $(" .manageContainer", {
                    display: "flex",
                    width: "100%",
                }),
                $(" .manageContainer > div", {
                    display: "grid",
                    width: "100%",
                }),
                $(" .checkboxContainer", {
                    height: "76px",
                    paddingTop: "34px",
                    marginBottom: "20px",
                }),
                $(" .selectTop", {
                    marginBottom: "20px",
                }),
                $(" .leftColumn", {
                    marginRight: "15px",
                }),
                $(" .rightColumn", {
                    marginLeft: "15px",
                }),
                $(" .modalDescription", {
                    fontSize: "14px",
                    color: theme.palette.darkGrey,
                    marginBottom: "20px",
                })
            ),
        };
    }, []);

    const muiStyles = React.useMemo(
        () => ({
            checkbox: {
                top: "5px",
            },
            menuItem: {
                paddingLeft: "50px",
            },
            popover: {
                width: "280px",
                height: "250px",
            },
        }),
        []
    );

    const [groupingSelect, setGroupingSelect] = React.useState<string>(doorGrouping || "");
    const [orderSelect, setOrderSelect] = React.useState(groupingClassificationOrder || Order.Ascending);
    const [filterSelectOptions, setFilterSelectOptions] = React.useState<Array<{ label: string; value: string }>>([]);
    const [filterSelect, setFilterSelect] = React.useState<{ [key: string]: boolean }>({});
    const [filterSelectType, setFilterSelectType] = React.useState<SelectionTypes>(SelectionTypes.All);

    const filterAvailableOptions = React.useCallback(
        (option: ISchemaKey): Array<{ label: string; value: string }> => {
            let hasEmptyOption = false;
            const result = [];

            for (const _option of lodashGet(option, SchemaField.FIELDS.ALLOWED_VALUES, [])) {
                hasEmptyOption = hasEmptyOption || !_option.string_value;

                if (_option.string_value) {
                    let label = _option.string_value;
                    // If PO Door group, label should be name instead of id
                    if (option.field_name === PURCHASE_ORDER_DOOR_GROUP_FIELD) {
                        label =
                            merchantPurchaseOrderDoorGroups.find(
                                (merchantPurchaseOrderDoorGroup) =>
                                    merchantPurchaseOrderDoorGroup.id === _option.string_value
                            )?.name ?? label;
                    }

                    result.push({
                        label,
                        value: _option.string_value,
                    });
                }
            }

            return result.concat(hasEmptyOption ? [{ value: "", label: "" }] : []);
        },
        [merchantPurchaseOrderDoorGroups]
    );

    React.useEffect(() => {
        const option = doorClassificationSchema.find((doorEntry) => doorEntry.field_name === doorGrouping);
        const _filterSelectOptions = filterAvailableOptions(option);
        const _filterSelect = filtersListToFiltersToShowObject(_filterSelectOptions, groupingClassificationHide);
        setFilterSelectOptions(_filterSelectOptions);
        setFilterSelect(_filterSelect);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        const _filterSelectType =
            Object.keys(filterSelect).length === 0
                ? SelectionTypes.None
                : Object.keys(filterSelect).length === filterSelectOptions.length
                  ? SelectionTypes.All
                  : SelectionTypes.Some;
        setFilterSelectType(_filterSelectType);
    }, [filterSelect]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleGroupingSelect = React.useCallback(
        (_event, index, value: string) => {
            setGroupingSelect(value);
            setOrderSelect(value === doorGrouping ? groupingClassificationOrder : Order.Ascending);
            let _filterSelectOptions = [];
            if (value) {
                const option = doorClassificationSchema[index - 1];
                _filterSelectOptions = filterAvailableOptions(option);
            }
            const _filterSelect = filtersListToFiltersToShowObject(
                _filterSelectOptions,
                value === doorGrouping ? groupingClassificationHide : []
            );
            setFilterSelectOptions(_filterSelectOptions);
            setFilterSelect(_filterSelect);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [doorClassificationSchema, groupingClassificationHide, doorGrouping]
    );

    const handleOrderSelect = React.useCallback((_event, _index, value) => {
        setOrderSelect(value);
    }, []);

    const handleFilterSelect = React.useCallback(
        (event) => {
            const { checked } = event.target;
            const value = event.target.getAttribute("data-value");
            let _filterSelect = { ...filterSelect };
            if (value === SELECT_ALL) {
                if (filterSelectType === SelectionTypes.All) {
                    _filterSelect = {};
                } else {
                    _filterSelect = filtersListToFiltersToShowObject(filterSelectOptions);
                }
            } else {
                if (checked) {
                    _filterSelect[value] = true;
                } else {
                    delete _filterSelect[value];
                }
            }
            setFilterSelect(_filterSelect);
        },
        [filterSelect, filterSelectType, filterSelectOptions]
    );

    const handleSaveParams = React.useCallback(() => {
        let actions = [];
        if (groupingSelect) {
            actions = [
                {
                    type: DimensionParamsActionType.ShowClassification,
                    value: groupingSelect,
                },
                {
                    type: DimensionParamsActionType.ChangeDirection,
                    value: orderSelect,
                },
                {
                    type: DimensionParamsActionType.HideClassification,
                    value: filtersToShowToFiltersToHideArray(filterSelectOptions, filterSelect),
                },
            ];
        } else {
            actions = [
                {
                    type: DimensionParamsActionType.ShowClassification,
                    value: undefined,
                },
                {
                    type: DimensionParamsActionType.ChangeDirection,
                    value: undefined,
                },
                {
                    type: DimensionParamsActionType.HideClassification,
                    value: undefined,
                },
            ];
        }
        const updated = processActionsToDimensionParams(actions, dimensionsPanelApplied);
        onApply(updated);
        onToggleManageDoorGrouping();
        onChangeCurrentView(itemDisplayProperty); // Reset current view
    }, [
        groupingSelect,
        orderSelect,
        filterSelect,
        filterSelectOptions,
        dimensionsPanelApplied,
        onToggleManageDoorGrouping,
        onApply,
        onChangeCurrentView,
        itemDisplayProperty,
    ]);

    const selectionRenderer = React.useCallback(
        (value: string[]) => {
            if (value.length === filterSelectOptions.length && filterSelectOptions.length > 0) {
                return formatMessage("Show all");
            } else {
                let labelFilterDropdown = value;
                if (groupingSelect === PURCHASE_ORDER_DOOR_GROUP_FIELD) {
                    labelFilterDropdown = filterSelectOptions.reduce((acc, filterSelected) => {
                        if (value.includes(filterSelected.value)) {
                            acc.push(filterSelected.label);
                        }

                        return acc;
                    }, []);
                }

                return labelFilterDropdown.join(", ").concat(value.includes("") ? `[${formatMessage("Blank")}]` : "");
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filterSelectOptions]
    );

    return (
        <Dialog
            title={formatMessage("Manage door grouping")}
            modal={false}
            open={open}
            actionsContainerClassName={`${styles.actions}`}
            contentClassName={`${styles.content}`}
            onRequestClose={onToggleManageDoorGrouping}
            actions={[
                <Button key="cancel" label={formatMessage("Cancel")} onClick={onToggleManageDoorGrouping} />,
                <Button
                    key="apply"
                    label={formatMessage("Show")}
                    primary={true}
                    className="applyButton"
                    onClick={handleSaveParams}
                />,
            ]}
        >
            <p className="modalDescription">{formatMessage("Select a label to group your doors by.")}</p>
            <Form className="manageContainer">
                <div className="leftColumn">
                    <SelectField
                        name="grouping"
                        value={groupingSelect}
                        autoWidth={true}
                        large={true}
                        label={formatMessage("Choose door grouping")}
                        baseClassName="selectTop"
                        onChange={handleGroupingSelect}
                    >
                        <MenuItem key="none" value={undefined} primaryText="" />
                        {doorClassificationSchema.map((groupingEntry: any) => (
                            <MenuItem
                                key={groupingEntry.field_name}
                                value={groupingEntry.field_name}
                                primaryText={groupingEntry.display_name}
                            />
                        ))}
                    </SelectField>
                    <SelectField
                        name="filter"
                        value={Object.keys(filterSelect)}
                        autoWidth={true}
                        large={true}
                        label={formatMessage("Filter")}
                        multiple={true}
                        selectionRenderer={selectionRenderer}
                        disabled={!groupingSelect}
                    >
                        <MenuItem
                            key={SELECT_ALL}
                            value={SELECT_ALL}
                            primaryText={formatMessage("Select all")}
                            leftCheckbox={
                                <Checkbox
                                    style={muiStyles.checkbox}
                                    onCheck={handleFilterSelect}
                                    data-value={SELECT_ALL}
                                    checked={filterSelectType !== SelectionTypes.None}
                                    checkedIcon={
                                        filterSelectType === SelectionTypes.Some ? <IndeterminateCheckBox /> : undefined
                                    }
                                />
                            }
                            style={muiStyles.menuItem}
                        />
                        <Divider key="divider" />
                        {filterSelectOptions.map((filterEntry: any) => {
                            const filterEntryValue = filterEntry.value;
                            const filterEntryLabel = filterEntry.label;
                            return (
                                <MenuItem
                                    key={filterEntryValue}
                                    value={filterEntryValue}
                                    primaryText={filterEntryLabel || `[${formatMessage("Blank")}]`}
                                    leftCheckbox={
                                        <Checkbox
                                            style={muiStyles.checkbox}
                                            onCheck={handleFilterSelect}
                                            checked={!!filterSelect[filterEntryValue]}
                                            data-value={filterEntryValue}
                                        />
                                    }
                                    style={muiStyles.menuItem}
                                />
                            );
                        })}
                    </SelectField>
                </div>
                <div className="rightColumn">
                    <SelectField
                        name="order"
                        value={orderSelect}
                        autoWidth={true}
                        large={true}
                        label={formatMessage("Order")}
                        onChange={handleOrderSelect}
                        disabled={!groupingSelect}
                    >
                        {Object.keys(Order).map((orderEntry) => (
                            <MenuItem key={Order[orderEntry]} value={Order[orderEntry]} primaryText={orderEntry} />
                        ))}
                    </SelectField>
                </div>
            </Form>
        </Dialog>
    );
};
